import { FC, useMemo } from "react";
import { Box } from "@mui/material";
import { AxisLabelsFormatterContextObject } from "highcharts";
import { HighchartsChartType } from "@/storybook/c8r-highcharts/common/components/HighchartsChartTypeSelect";
import {
  NormalizedWidgetAggregatedCost,
  WidgetNormalizedCostVisualization,
} from "@/services/cloudchipr.api";
import { HighchartsProvider } from "@/storybook/c8r-highcharts/common/components/HighchartsProvider";
import { generateHighChartsDataForNormalizedCostWidget } from "@/components/pages/dashboard/components/widgets/normalized-cost-widget/utils/helpers/generateHighChartsDataForNormalizedCostWidget";
import { MultiTypeHighcharts } from "@/storybook/c8r-highcharts/MultiTypeHighcharts";
import { formatDate } from "@/utils/helpers/date-time/datetime-format";
import { moneyFormatter } from "@/utils/numeral/moneyFormatter";
import { stackedChartHeadingLabelFormatter } from "@/storybook/c8r-highcharts/common/utils/helpers/stackedChartHeadingLabelFormatter";

import { getHighChartsColumnDataLabels } from "@/storybook/c8r-highcharts/common/utils/helpers/getHighChartsColumnDataLabels";

import { getNormalizedCostWidgetTooltipValueFormatter } from "@/components/pages/dashboard/components/widgets/normalized-cost-widget/utils/helpers/getNormalizedCostWidgetTooltipValueFormatter";

interface NormalizedCostWidgetHighChartProps {
  loading?: boolean;
  showLegends?: boolean;
  hasAggregations?: boolean;
  chartType: HighchartsChartType;
  regular?: NormalizedWidgetAggregatedCost[];
  normalized?: NormalizedWidgetAggregatedCost[];
  scale?: WidgetNormalizedCostVisualization["scale"];
}

export const NormalizedCostWidgetHighChart: FC<
  NormalizedCostWidgetHighChartProps
> = ({
  scale,
  loading,
  regular,
  chartType,
  normalized,
  showLegends,
  hasAggregations,
}) => {
  const chartData = useMemo(
    () => generateHighChartsDataForNormalizedCostWidget(regular, normalized),
    [regular, normalized],
  );

  const dataMap = useMemo(
    () =>
      new Map([
        ["Actual Cost", regular ?? []],
        ["Normalized Cost", normalized ?? []],
      ]),
    [normalized, regular],
  );

  const tooltipValueFormatter = useMemo(
    () => getNormalizedCostWidgetTooltipValueFormatter(dataMap),
    [dataMap],
  );

  const columnDataLabels = useMemo(
    () => getHighChartsColumnDataLabels(chartType, "cost"),
    [chartType],
  );

  return (
    <Box height={hasAggregations ? 370 : 445}>
      <HighchartsProvider>
        <MultiTypeHighcharts
          loading={loading}
          chartScale={scale}
          chartType={chartType}
          chartData={chartData}
          plotSeries={plotSeries}
          showLegends={showLegends}
          tooltipOptions={tooltipOptions}
          columnDataLabels={columnDataLabels}
          yAxisLabelsFormatter={yAxisLabelsFormatter}
          xAxisLabelsFormatter={xAxisLabelsFormatter}
          tooltipMarkupGenerator={tooltipValueFormatter}
          noDataMessage="No data by your selected date range."
          stackedChartHeadingLabelFormatter={stackedChartHeadingLabelFormatter}
        />
      </HighchartsProvider>
    </Box>
  );
};

const tooltipOptions = {
  shared: true,
};

const plotSeries = {
  point: {},
};

const xAxisLabelsFormatter = (params: AxisLabelsFormatterContextObject) => {
  return formatDate(String(params.value), { type: "shortMonthYear" });
};

const yAxisLabelsFormatter = (params: AxisLabelsFormatterContextObject) => {
  return String(moneyFormatter(+params.value));
};
